import React, { useState, useEffect } from 'react';
import styles from './LiveNow.module.css';
import defaultProfileImage from '../../assets/default_profile.png';
import defaultThumbnail from '../../assets/default_thumbnail.png';
import YouTubeClipsList from '../molecules/YouTubeClipsList.jsx';
import TwitchClipsList from '../molecules/TwitchClipsList.jsx';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://hp.api.doberan.jp';

/**
 * 配信状況を表示するコンポーネント
 * @param {object} props
 * @param {function(boolean, boolean): void} props.onStatusChange - 配信状態とエラー状態の変更を通知するコールバック
 * @param {Array<object>} props.youtubeClips - YouTubeクリップの配列
 * @param {Array<object>} props.latestTwitchClips - 最新Twitchクリップの配列
 * @param {Array<object>} props.popularTwitchClips - 人気Twitchクリップの配列
 */
const LiveNow = ({ onStatusChange, youtubeClips = [], latestTwitchClips = [], popularTwitchClips = [] }) => {
  const [streamStatus, setStreamStatus] = useState({
    is_live: false,
    image_url: '',
    stream_title: ''
  });
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    // react-snapによるプリレンダリング中はAPIフェッチをスキップ
    const isPrerendering = navigator.userAgent === 'ReactSnap';
    if (isPrerendering) {
      return;
    }

    const fetchStreamStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/twitch`);
        if (!response.ok) {
          throw new Error('配信状況の取得に失敗しました');
        }
        const data = await response.json();
        setStreamStatus(data.stream);
        setImageError(false);
        if (onStatusChange) {
          onStatusChange(data.stream.is_live, false);
        }
      } catch (err) {
        setError(err.message);
        if (onStatusChange) {
          onStatusChange(false, true);
        }
      }
    };

    fetchStreamStatus();
    // 1分ごとに更新（APIのキャッシュ時間に合わせる）
    const interval = setInterval(fetchStreamStatus, 60000);
    return () => clearInterval(interval);
  }, [onStatusChange]);

  const handleImageError = () => {
    setImageError(true);
  };

  const displayImage = streamStatus.is_live ? (imageError ? defaultThumbnail : streamStatus.image_url) : defaultProfileImage;
  const displayAltText = streamStatus.is_live ? "配信サムネイル" : "プロフィール画像";
  const displayClassName = streamStatus.is_live ? styles.thumbnail : styles.profileImage;
  const displayText = streamStatus.is_live ? 'どべらんチャンネルで視聴' : (error ? '配信状況の取得に失敗しました' : 'どべらんチャンネルへ');

  return (
    <div className={styles.container}>
      <div className={styles.streamSection}>
        <a 
          href="https://www.twitch.tv/doberan_games" 
          target="_blank" 
          rel="noopener noreferrer"
          className={`${styles.link} ${streamStatus.is_live ? styles.isLive : ''}`}
        >
          <img 
            src={displayImage}
            alt={displayAltText}
            className={displayClassName}
            onError={streamStatus.is_live ? handleImageError : undefined}
          />
          <span>{displayText}</span> 
        </a>
      </div>
      <div className={styles.clipsSection}>
        <div className={styles.youtubeClips}>
          <YouTubeClipsList clips={youtubeClips} />
        </div>
        <div className={styles.twitchClips}>
          <h3 className={styles.clipTitleHeading}>最新Twitchクリップ</h3>
          <TwitchClipsList clips={latestTwitchClips} />
          <h3 className={styles.clipTitleHeading}>人気Twitchクリップ</h3>
          <TwitchClipsList clips={popularTwitchClips} />
        </div>
      </div>
    </div>
  );
};

export default LiveNow; 